import { useCallback, useEffect } from 'react';
import {
    Alert, Col, FormGroup, Spinner, Table, UncontrolledTooltip,
} from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { faArrowRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../Common';
import {
    FormLabel,
    GroupedRow,
    FormGroupColumn,
    NumericInput,
    IconButton,
    Input,
    toasty,
} from './forms/FormElements';
import ValidatedSelect from './forms/ValidatedSelect';
import { TipAlert } from './TipAlert';

const RentalPickupReasons = [
    { label: 'Missing/Stolen/Damaged', value: 'Missing/Stolen/Damaged' },
    { label: 'Incorrect drop-off quantity', value: 'Incorrect drop-off quantity' },
    { label: 'Picked up but not recorded', value: 'Picked up but not recorded' },
    { label: 'Returned by Client', value: 'Returned by Client' },
];

const buildPickupResources = (projectResources) => (projectResources ?? [])
    .filter((rs) => rs.actualQuantity > 0)
    .map((rs) => ({
        id: rs.id,
        resourceName: rs.equipmentTypeName,
        resourceIds: rs.resourceIds,
        onsite: rs.actualQuantity,
        quantity: 0,
        chargeTypeId: null,
        equipmentIds: [],
        equipmentTypeId: rs.equipmentTypeId,
        isGeneric: rs.isGeneric,
    }));

const computeMinDate = (project) => {
    if (project?.lastInvoiced) return moment(project.lastInvoiced).add(1, 'day').toDate();
    if (project?.billingStartDate) return moment(project.billingStartDate).toDate();
    return new Date();
};

export const EquipmentPickupForm = ({
    project,
    contractId,
    pickupDetails,
    setPickupDetails,
}) => {
    const projectId = project?.id;

    const {
        data: lastTimesheet,
        isLoading: lastTimesheetLoading,
        isSuccess: lastTimesheetSuccess,
        error: lastTimesheetError,
    } = useQuery({
        queryKey: ['equipmentPickup', 'lastSubmittedTimesheet', projectId],
        queryFn: () => httpClient.js(ApiRoutes.project.getLastSubmittedTimesheet(projectId)),
        enabled: !!projectId,
        select: (resp) => resp?.data ?? null,
    });

    const {
        data: chargeTypes = [],
        isLoading: chargeTypesLoading,
        error: chargeTypesError,
    } = useQuery({
        queryKey: ['equipmentPickup', 'chargeTypes', contractId],
        queryFn: () => httpClient.js(ApiRoutes.chargeType.byContract(contractId)),
        enabled: !!contractId,
    });

    const {
        data: equipments = [],
        isLoading: equipmentsLoading,
        error: equipmentsError,
    } = useQuery({
        queryKey: ['equipmentPickup', 'projectPickupEquipment', projectId],
        queryFn: () => httpClient.js(ApiRoutes.timesheet.projectPickupEquipment(projectId)),
        enabled: !!projectId,
    });

    const isLoading = lastTimesheetLoading || chargeTypesLoading || equipmentsLoading;
    const firstError = lastTimesheetError || chargeTypesError || equipmentsError;
    const lastTimesheetMissing = lastTimesheetSuccess && !lastTimesheet?.id;
    const hasEquipmentOnSite = (project?.resources ?? []).some((rs) => rs.actualQuantity > 0);
    const minDate = computeMinDate(project);
    const timesheetNumber = lastTimesheet?.timesheetNumber ?? null;

    useEffect(() => {
        if (pickupDetails) return;
        if (isLoading || firstError) return;
        if (!lastTimesheet?.id || !hasEquipmentOnSite) return;

        setPickupDetails({
            parentId: lastTimesheet.id,
            pickupDate: new Date(),
            reason: null,
            resources: buildPickupResources(project?.resources),
        });
    }, [
        pickupDetails,
        setPickupDetails,
        isLoading,
        firstError,
        lastTimesheet,
        hasEquipmentOnSite,
        project,
    ]);

    const handleDateChange = useCallback((e) => {
        setPickupDetails((prev) => ({ ...prev, pickupDate: e.target.value }));
    }, [setPickupDetails]);

    const onPickupReasonSelectChanged = useCallback((reason) => {
        setPickupDetails((prev) => ({ ...prev, reason: reason?.value ?? null }));
    }, [setPickupDetails]);

    const handlePickupQuantityChange = useCallback((id, value) => {
        setPickupDetails((prev) => {
            const resources = prev.resources.map((rs) => {
                if (rs.id !== id) return rs;
                const updated = { ...rs, quantity: Number(value) };
                if (!rs.isGeneric && rs.quantity > Number(value)) {
                    updated.equipmentIds = [];
                }
                return updated;
            });
            return { ...prev, resources };
        });
    }, [setPickupDetails]);

    const handlePickupChargeChange = useCallback((id, value) => {
        setPickupDetails((prev) => ({
            ...prev,
            resources: prev.resources.map((rs) => (
                rs.id === id ? { ...rs, chargeTypeId: value } : rs
            )),
        }));
    }, [setPickupDetails]);

    const handlePickupEquipmentChange = useCallback((id, multi, value) => {
        setPickupDetails((prev) => ({
            ...prev,
            resources: prev.resources.map((rs) => (
                rs.id === id
                    ? { ...rs, equipmentIds: multi ? [...value] : [value] }
                    : rs
            )),
        }));
    }, [setPickupDetails]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center my-3">
                <Spinner />
            </div>
        );
    }

    if (firstError) {
        return (
            <Alert color="danger">
                {firstError.message || 'Failed to load pickup data.'}
            </Alert>
        );
    }

    if (lastTimesheetMissing) {
        return (
            <Alert color="danger">
                No submitted timesheets found for this project.
            </Alert>
        );
    }

    if (!hasEquipmentOnSite) {
        return (
            <Alert color="danger">
                There is no equipment currently on site for this project.
            </Alert>
        );
    }

    if (!pickupDetails) return null;

    return (
        <>
            <GroupedRow>
                <FormGroupColumn>
                    <Input
                        label="Pickup Date"
                        id="pickupDate"
                        name="pickupDate"
                        type="date"
                        min={moment(minDate).format('YYYY-MM-DD')}
                        max={moment().format('YYYY-MM-DD')}
                        value={
                            pickupDetails.pickupDate
                                ? new Date(pickupDetails.pickupDate).toISOString().split('T')[0]
                                : ''
                        }
                        onChange={handleDateChange}
                        required
                        error="A pickup date is required."
                    />
                </FormGroupColumn>

                <FormGroupColumn>
                    <ValidatedSelect
                        id="pickupReason"
                        name="pickupReason"
                        label="Reason"
                        options={RentalPickupReasons}
                        value={
                            RentalPickupReasons.find(
                                (x) => x.value === pickupDetails.reason,
                            ) ?? ''
                        }
                        onChange={onPickupReasonSelectChanged}
                    />
                </FormGroupColumn>
            </GroupedRow>

            <GroupedRow>
                <Col>
                    <FormGroup>
                        <FormLabel
                            htmlFor="resources"
                            label="Resources"
                        />
                        <div className="table-overflow-visible">
                            <Table
                                bordered
                                responsive
                                size="sm"
                            >
                                <thead>
                                    <tr>
                                        <th>Resource</th>
                                        <th># On Site</th>
                                        <th>Quantity Picked Up</th>
                                        <th>Charge</th>
                                        <th>Equipment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pickupDetails.resources?.length > 0 && (
                                        pickupDetails.resources.map((resource, index) => {
                                            const filteredChargeTypes = chargeTypes
                                                ?.filter(
                                                    (item) => item.equipmentTypes.some((et) => et.id === resource.equipmentTypeId),
                                                )
                                                .map((item) => ({
                                                    value: item.id,
                                                    label: item.information,
                                                })) ?? [];
                                            const filteredEquipment = equipments
                                                ?.filter(
                                                    (item) => item.equipmentTypeId === resource.equipmentTypeId,
                                                )
                                                .map((item) => ({
                                                    value: item.id,
                                                    label: item.description,
                                                })) ?? [];
                                            return (
                                                <tr key={resource.id}>
                                                    <td>{resource.resourceName}</td>
                                                    <td className="text-center">{resource.onsite}</td>
                                                    <td>
                                                        <NumericInput
                                                            min={0}
                                                            max={resource.onsite}
                                                            step={1}
                                                            wholeNumber
                                                            className="text-right"
                                                            value={resource.quantity}
                                                            onChange={(e) => handlePickupQuantityChange(resource.id, e.target.value)}
                                                            disabled={!resource.isGeneric}
                                                            endAddon={(
                                                                <>
                                                                    <IconButton
                                                                        id={`pickup-remaining-${resource.id}`}
                                                                        type="button"
                                                                        disabled={!resource.isGeneric}
                                                                        onClick={() => handlePickupQuantityChange(resource.id, resource.onsite)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faArrowRightFromBracket} />
                                                                    </IconButton>

                                                                    <UncontrolledTooltip target={`pickup-remaining-${resource.id}`}>
                                                                        Pick up all remaining equipment
                                                                    </UncontrolledTooltip>
                                                                </>
                                                            )}
                                                        />
                                                    </td>
                                                    <td>
                                                        <ValidatedSelect
                                                            name={`chargePickup[${index}]`}
                                                            key={`chargePickup[${index}]`}
                                                            hideClear
                                                            options={filteredChargeTypes}
                                                            value={
                                                                filteredChargeTypes.find((x) => resource.chargeTypeId == x.value)
                                                                ?? ''
                                                            }
                                                            onChange={(selected) => {
                                                                handlePickupChargeChange(resource.id, selected?.value);
                                                            }}
                                                            required={resource.quantity > 0}
                                                            error="A charge type is required."
                                                        />
                                                    </td>
                                                    <td>
                                                        {resource.isGeneric ? (
                                                            <ValidatedSelect
                                                                name={`equipmentPickup[${index}]`}
                                                                key={`equipmentPickup[${index}]`}
                                                                hideClear
                                                                options={filteredEquipment}
                                                                value={
                                                                    filteredEquipment.find(
                                                                        (x) => (resource.equipmentIds ?? []).includes(x.value),
                                                                    ) ?? ''
                                                                }
                                                                onChange={(selected) => {
                                                                    handlePickupEquipmentChange(resource.id, false, selected?.value);
                                                                }}
                                                                required={resource.quantity > 0}
                                                                error="A pickup equipment is required."
                                                            />
                                                        ) : (
                                                            <ValidatedSelect
                                                                name={`equipmentPickup[${index}]`}
                                                                key={`equipmentPickup[${index}]`}
                                                                hideClear
                                                                options={filteredEquipment}
                                                                value={
                                                                    filteredEquipment.filter(
                                                                        (x) => (resource.equipmentIds ?? []).includes(x.value),
                                                                    )
                                                                }
                                                                onChange={(selected) => {
                                                                    let value = Array.isArray(selected)
                                                                        ? selected.map((s) => s.value)
                                                                        : selected?.value;
                                                                    if (value.length > resource.onsite) {
                                                                        toasty.warning(
                                                                            `You can select up to ${resource.onsite} equipment(s).`,
                                                                        );
                                                                        value = value.slice(0, resource.onsite);
                                                                    }
                                                                    handlePickupQuantityChange(resource.id, value.length);
                                                                    handlePickupEquipmentChange(resource.id, true, value);
                                                                }}
                                                                isMulti
                                                                required={resource.quantity > 0}
                                                                error="A pickup equipment is required."
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                            {timesheetNumber && (
                                <TipAlert
                                    tipText="Note"
                                    label={`This action adds an approved timesheet to the job and links it to the last submitted timesheet ${timesheetNumber}.`}
                                />
                            )}
                        </div>
                    </FormGroup>
                </Col>
            </GroupedRow>
        </>
    );
};
